/*
PURPOSE: Pick which contribution-fee producer the coral creator's receipt reads from.

USE CASES:
- The creator card, which shows a receipt beside per-artist amounts and must agree with them

DOMAIN BOUNDARIES:
- Selection only. No fee arithmetic of its own: both branches call the shared producer

ASSUMPTIONS:
- A real simulation always reports a non-zero transaction fee, because processing carries a
  fixed component. Zero therefore means no simulation has run
*/

import {
  computeContributionFeeBreakdown,
  contributionBreakdownFromStored,
  type ContributionFeeBreakdown,
} from 'shared/utils/coralContributionFee';

/** The two figures the creator's simulation reports, in dollars. */
export interface CreatorSimulation {
  transactionFee: number;
  platformFee: number;
}

/**
 * The split to display for a pledge the member has not paid yet.
 *
 * Once the simulation has run, its figures win: it distributes the artist pool to whole cents
 * and absorbs the remainders into the fee, so only its numbers agree with the per-artist rows
 * the card shows. The modelled producer stands in before then, when the simulation reports no
 * fees at all and a receipt built from it would claim the whole pledge reaches artists.
 */
export function creatorBreakdown(
  pledgeAmountDollars: number,
  simulation: CreatorSimulation,
): ContributionFeeBreakdown {
  if (simulation.transactionFee > 0) {
    return contributionBreakdownFromStored({
      pledgeAmount: pledgeAmountDollars,
      transactionFee: simulation.transactionFee,
      platformFee: simulation.platformFee,
      contributionDate: new Date().toISOString(),
    });
  }
  return computeContributionFeeBreakdown(Math.round(pledgeAmountDollars * 100));
}
